import React from "react";
import { Action, Dispatch } from "redux";
import jsYaml from "js-yaml";
import {
  ParseClintDefinitionForSelection,
  GetRulesAndFilters,
  ParseClintDefinitionForDate,
  GetDateDescrition,
  parseAgeFilter,
  getAgeHeaderText,
} from "@emisgroup/cohort-builder";
import { EthnicityType } from "@emisgroup/cohort-builder-kit";
import {
  TableProps,
  TableDataRowProps,
  TableHeaderProps
} from "../../../Common/UI/Index";
import { OutputConfigSlice } from "../redux";
import { OutputConfigRemove } from "../OutputConfigLandingPage/OutputConfigList/RemoveOutputConfig.Logic";
import { DeleteTypes } from "../OutputConfigLandingPage/OutputConfigList/Constants";
import { STATUS } from "../Constant";
import { Module } from "../../../Types";
import {
  AGE_FEATURE,
  AGE_IS,
  APP_KEY,
  DATE_FEATURE,
  GENDER_FEATURE,
  PATIENT_DETAILS,
  RANGE_FROM,
  RANGE_TO,
} from "./Constant";
import { BandModeType, BandsTypes } from "./Types";
import "../../OutputConfig/Style.scss";
import { CapitalizeEthnicityCategory } from "./Utilities";
import Edit from "~icons/ic/outline-edit";


export interface SelectionItem {
  /**
   * Document text of the selected item
   */
  documentText: string;
  /**
   * Display text of the selected item
   */
  textValue: string;
  /**
   * Value of the selected item
   */
  value: string;
}

export interface BandingDetailsProps {
  /**
   * ID of the band
   */
  bandGuid: string;
  /**
   * Name of the band
   */
  name: string;
  /**
   * Band definition
   */
  definition?: string;
  /**
   * Band label
   */
  label?: string;
  /**
   * Type of the band
   */
  bandType?: BandsTypes;
  /**
   * Mode of the band
   */
  bandMode?: BandModeType;
  /**
   * Filter of the ethnicity
   */
  ethnicityCategory?: EthnicityType;
  /*
   * Status of the band
   */
  status?: string;
}

/**
 * To render the action column icons
 * @param item Edited item
 * @param dispatch callback function to dispatch
 * @param bandType selected band type
 * @returns the icons
 * NOTE: Type any is used because UI Table accepts only strings as values
 * but we need icons in the table row
 */
const getActionColumn = (
  item: BandingDetailsProps,
  dispatch: Dispatch<Action>,
  bandType: BandsTypes
): any => {
  /**
   * Handler for band edit
   */
  const handleEditBand = (): void => {
    const ethnicityCategory: EthnicityType = "ETHNICITY";
    item.bandType = bandType;
    item.bandMode = BandModeType.EDIT;
    if (bandType === BandsTypes.ETHNICITY) {
      item.ethnicityCategory = ethnicityCategory;
    }
    dispatch(OutputConfigSlice.actions.setSelectedBandingDetails(item));
  };

  return (
    <div className="make-flex">
      <Edit
        title="edit"
        onClick={handleEditBand}
        className="ebt-output-config__list__edit-icon"
      />
      <div className="ebt-output-config__list__delete-icon">
        <OutputConfigRemove deleteType={DeleteTypes.DELETE_BAND} bandItem={item} />
      </div>
    </div>
  );
};

/**
 * To parse the clint definition
 * @param definition clint
 * @param featureType feature type
 * @param module module
 * @returns the formatted selection text
 */
const parseClintDefinition = (definition: string, featureType: string, module: string): string => {
  GetRulesAndFilters(APP_KEY);
  const parseDefinition: any = jsYaml.load(definition);
  const runParam = parseDefinition?.run?.[0];
  const selectionList = ParseClintDefinitionForSelection((runParam || definition), featureType, module);
  return selectionList?.[0]?.selectedValues?.map((item: SelectionItem) => item.textValue).join(",");
};

/**
 * Method to handle styling with respect to the status(Replaced empty space with - to match with the classname)
 * @param statusType Banding status
 * @returns JSX element
 */
const getStatus = (statusType: string): JSX.Element => {
  const formattedStatus = statusType.replace(/\s/g, "-");
  return <div className={`ebt-config-status__${formattedStatus}`}>{statusType}</div>;
};

/**
 * To get the date text
 * @param definition clint definition
 * @param isToDisplayText flag to display text
 * @returns the date text
 */
const getDateRange = (definition: string, isToDisplayText: boolean): string | any => {
  let ageDefinitionFeature = {};
  ageDefinitionFeature = ParseClintDefinitionForDate(definition, DATE_FEATURE, Module.PatientDetails);
  if (isToDisplayText) {
    return GetDateDescrition(ageDefinitionFeature?.[0]?.dateRange?.date);
  } else {
    return ageDefinitionFeature;
  }
};

/**
 * To get the age text
 * @param definition clint definition
 * @param isToDisplayText flag to display text
 * @returns the age text
 */
const getAgeValue = (definition: string, isToDisplayText: boolean): string | any => {
  let formattedDefinition = definition;
  const isAgeWithDate = definition?.includes(AGE_IS);
  let displayText = "";
  const ageObject = parseAgeFilter(definition, AGE_FEATURE, Module.PatientDetails);
  const ageText = ageObject ? getAgeHeaderText(ageObject?.[0]) : "";

  /**
   * If the clint definition has both date and age
   * Need to split them as age and date
   * Then we need to extract the display text for each feature
   */
  if (isAgeWithDate) {
    // Need to replace the age on date with date to get the date display text
    formattedDefinition = definition?.split(AGE_IS)?.[0]?.replace(
      ":age:on_date:", ":date:"
    );
    const dateText = getDateRange(formattedDefinition, isToDisplayText);
    displayText = `${ageText} on ${dateText}`;
  } else {
    displayText = ageText;
  }

  if (isToDisplayText) {
    return displayText;
  } else {
    return { ...ageObject };
  }
};

/**
 * To get the age band definition
 * @param defintion clint definition
 * @param splittedDefinition range(from or to) flag
 * @param isToDisplayText flag to display text
 * @returns the display text
 */
const getAgeDefinition = (defintion: string, splittedDefinition: string, isToDisplayText: boolean): string => {
  if (splittedDefinition === DATE_FEATURE) {
    return getDateRange(defintion, isToDisplayText);
  } else if (splittedDefinition === AGE_FEATURE) {
    return getAgeValue(defintion, isToDisplayText);
  }
  return "-";
};

/**
 * To get the age band definition from clint definition
 * @param definition clint definition
 * @param isToDisplayText flag to display text
 * @returns the age feature or date feature
 */
export const GetAgeBandFeatureFromClint = (
  definition: string,
  isToDisplayText: boolean
): string | any => {
  let ageFeatureForFrom, ageFeatureForTo;

  if (definition) {
    const parseDefinition: any = jsYaml.load(definition);
    const runParam = parseDefinition?.run;
    const designParam = parseDefinition?.design?.[0];
    let rangeFromDefinition, rangeToDefinition, splittedFromDef, splittedToDef;
    if (runParam.length === 2) {
      rangeFromDefinition = runParam[0];
      rangeToDefinition = runParam[1];
      splittedFromDef = rangeFromDefinition?.split(":")?.[1];
      splittedToDef = rangeToDefinition?.split(":")?.[1];
      ageFeatureForFrom = getAgeDefinition(rangeFromDefinition, splittedFromDef, isToDisplayText);
      ageFeatureForTo = getAgeDefinition(rangeToDefinition, splittedToDef, isToDisplayText);
    } else {
      rangeFromDefinition = runParam[0];
      rangeToDefinition = runParam[0];
      splittedFromDef = rangeFromDefinition?.split(":")?.[1];
      splittedToDef = rangeToDefinition?.split(":")?.[1];
      if (designParam === RANGE_FROM) {
        ageFeatureForFrom = getAgeDefinition(rangeFromDefinition, splittedFromDef, isToDisplayText);
      } else if (designParam === RANGE_TO) {
        ageFeatureForTo = getAgeDefinition(rangeToDefinition, splittedToDef, isToDisplayText);
      }
    }
  }

  return { ageFeatureForFrom, ageFeatureForTo };
};

/**
 * To bind the table data
 * @param data response data
 * @param dispatch callback function to dispatch
 * @returns the formatted table row data
 */
const buildBandingListTableRows = (data: string, dispatch: Dispatch<Action>): TableDataRowProps[] => {
  GetRulesAndFilters(APP_KEY);
  const response = JSON.parse(data);
  let outputData: TableDataRowProps[] = [];
  const inputData = response && response.ageBand;
  outputData = inputData?.map((item: BandingDetailsProps) => {
    let displayTextForFromRange, displayTextForToRange;
    if (item?.definition && item?.definition?.length > 0) {
      const { ageFeatureForFrom, ageFeatureForTo } = GetAgeBandFeatureFromClint(item.definition, true);
      displayTextForFromRange = ageFeatureForFrom;
      displayTextForToRange = ageFeatureForTo;
    }
    const output: TableDataRowProps = {
      row: [
        {
          value: item.name,
          propertyName: "name",
        },
        {
          value: displayTextForFromRange || "-",
          propertyName: "rangeFrom",
        },
        {
          value: displayTextForToRange || "-",
          propertyName: "rangeTo",
        },
        {
          value: getStatus(item.status as string),
          propertyName: STATUS,
        },
        {
          value: getActionColumn(item, dispatch, BandsTypes.AGE),
          propertyName: "actions",
        }
      ]
    };
    return output;
  });

  return outputData;
};

/**
 * To populate the table data
 * @param data response data
 * @param dispatch callback function to dispatch
 * @returns the formatted data to bind the table
 */
export const PopulateBandListTable = (data: string, dispatch: Dispatch<Action>): TableProps => {
  let table: TableProps = {} as TableProps;
  const tableHeader: TableHeaderProps[] = [
    {
      title: "Name",
      propertyName: "name",
      width: "30%"
    },
    {
      title: "Range from",
      propertyName: "rangeFrom",
      width: "25%"
    },
    {
      title: "Range to",
      propertyName: "rangeTo",
      width: "25%"
    },
    {
      title: STATUS,
      propertyName: STATUS,
      width: "10%"
    },
    {
      title: "Actions",
      propertyName: "actions",
      width: "10%"
    }
  ];

  table = {
    headerRow: tableHeader,
    dataRow: buildBandingListTableRows(data, dispatch)
  };

  return table;
};

/**
 * To bind the gender table data
 * @param data response data
 * @param dispatch callback function to dispatch
 * @returns the formatted table row data
 */
const buildGenderTableRows = (data: string, dispatch: Dispatch<Action>): TableDataRowProps[] => {
  const response = JSON.parse(data);
  let outputData: TableDataRowProps[] = [];
  const inputData = response && response.genderBand;
  outputData = inputData?.map((item: BandingDetailsProps) => {
    const output: TableDataRowProps = {
      row: [
        {
          value: item.name,
          propertyName: "name",
        },
        {
          value: parseClintDefinition(item?.definition || "", GENDER_FEATURE, PATIENT_DETAILS) || "-",
          propertyName: "Gender",
        },
        {
          value: getStatus(item.status as string),
          propertyName: STATUS,
        },
        {
          value: getActionColumn(item, dispatch, BandsTypes.GENDER),
          propertyName: "actions",
        }
      ]
    };
    return output;
  });

  return outputData;
};

/**
 * To populate the gender table data
 * @param data response data
 * @param dispatch callback function to dispatch
 * @returns the formatted data to bind the table
 */
export const PopulateGenderListTable = (data: string, dispatch: Dispatch<Action>): TableProps => {
  let table: TableProps = {} as TableProps;
  const tableHeader: TableHeaderProps[] = [
    {
      title: "Name",
      propertyName: "name",
      width: "55%"
    },
    {
      title: "Gender",
      propertyName: "gender",
      width: "25%"
    },
    {
      title: STATUS,
      propertyName: STATUS,
      width: "10%"
    },
    {
      title: "Actions",
      propertyName: "actions",
      width: "10%"
    }
  ];

  table = {
    headerRow: tableHeader,
    dataRow: buildGenderTableRows(data, dispatch)
  };

  return table;
};

/**
 * To bind the ethinicity table data
 * @param data response data
 * @param dispatch callback function to dispatch
 * @returns the formatted table row data
 */
const buildEthinicityTableRows = (data: string, dispatch: Dispatch<Action>): TableDataRowProps[] => {
  const response = JSON.parse(data);
  let outputData: TableDataRowProps[] = [];
  const inputData = response && response.ethnicityBand;
  outputData = inputData?.map((item: BandingDetailsProps) => {
    const parseDefinition: any = jsYaml.load(item.definition as string);
    const runParam = parseDefinition?.run;
    const runDefinition = Array.isArray(runParam) ? runParam?.[0]?.split(":") : runParam?.split(":");
    const ethnicityList = runDefinition?.slice(1)?.join(", ");
    const output: TableDataRowProps = {
      row: [
        {
          value: item.name,
          propertyName: "name",
        },
        {
          value: ethnicityList || "-",
          propertyName: "ethinicity",
        },
        {
          value: CapitalizeEthnicityCategory(item?.ethnicityCategory as string) || "-",
          propertyName: "ethnicityCategory",
        },
        {
          value: getStatus(item.status as string),
          propertyName: STATUS,
        },
        {
          value: getActionColumn(item, dispatch, BandsTypes.ETHNICITY),
          propertyName: "actions",
        }
      ]
    };
    return output;
  });

  return outputData;
};

/**
 * To populate the ethinicity table data
 * @param data response data
 * @param dispatch callback function to dispatch
 * @returns the formatted data to bind the table
 */
export const PopulateEthinicityListTable = (data: string, dispatch: Dispatch<Action>): TableProps => {
  let table: TableProps = {} as TableProps;
  const tableHeader: TableHeaderProps[] = [
    {
      title: "Name",
      propertyName: "name",
      width: "30%"
    },
    {
      title: "Ethinicity code list",
      propertyName: "ethinicity",
      width: "25%"
    },
    {
      title: "Ethinicity category",
      propertyName: "ethnicityCategory",
      width: "25%"
    },
    {
      title: STATUS,
      propertyName: STATUS,
      width: "10%"
    },
    {
      title: "Actions",
      propertyName: "actions",
      width: "10%"
    }
  ];

  table = {
    headerRow: tableHeader,
    dataRow: buildEthinicityTableRows(data, dispatch)
  };

  return table;
};
